/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import axios from './axios';
import { handleAxiosError, serverUrl } from './axios-service';
import {
  LoginRequest,
  LoginResponse,
  UserCreate,
  UserResponse,
  UserUpdate,
  ChangePasswordRequest,
  AdminChangePasswordRequest,
} from '../types/user';

const baseUrl = `${serverUrl}/users`;

const useUserApi = () => {
  const login = useCallback(async (data: LoginRequest): Promise<LoginResponse> => {
    try {
      const formData = new URLSearchParams();
      formData.append('username', data.username);
      formData.append('password', data.password);

      const result: AxiosResponse<LoginResponse> = await axios.post(
        `${baseUrl}/login`,
        formData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );
      return result.data;
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }, []);

  const register = useCallback(async (data: UserCreate): Promise<UserResponse> => {
    try {
      const result: AxiosResponse<UserResponse> = await axios.post(
        `${baseUrl}/register`,
        data
      );
      return result.data;
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }, []);

  const createUser = useCallback(async (data: UserCreate): Promise<UserResponse> => {
    try {
      const result: AxiosResponse<UserResponse> = await axios.post(
        `${baseUrl}/create-user`,
        data
      );
      return result.data;
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }, []);

  const getCurrentUser = useCallback(async (): Promise<UserResponse> => {
    try {
      const result: AxiosResponse<UserResponse> = await axios.get(
        `${baseUrl}/me`
      );
      return result.data;
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }, []);

  const getUser = useCallback(async (userId: string): Promise<UserResponse> => {
    try {
      const result: AxiosResponse<UserResponse> = await axios.get(
        `${baseUrl}/${userId}`
      );
      return result.data;
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }, []);

  const updateUser = useCallback(async (userId: string, data: UserUpdate): Promise<UserResponse> => {
    try {
      const result: AxiosResponse<UserResponse> = await axios.put(
        `${baseUrl}/${userId}`,
        data
      );
      return result.data;
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }, []);

  const updateProfile = useCallback(async (data: UserUpdate): Promise<UserResponse> => {
    try {
      const result: AxiosResponse<UserResponse> = await axios.put(
        `${baseUrl}/profile`,
        data
      );
      return result.data;
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }, []);

  const deleteUser = useCallback(async (userId: string): Promise<void> => {
    try {
      await axios.delete(`${baseUrl}/${userId}`);
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }, []);

  const listUsers = useCallback(async (skip = 0, limit = 1000): Promise<UserResponse[]> => {
    try {
      const result: AxiosResponse<UserResponse[]> = await axios.get(
        `${baseUrl}?skip=${skip}&limit=${limit}`
      );
      return result.data;
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }, []);

  const changePassword = useCallback(async (data: ChangePasswordRequest): Promise<void> => {
    try {
      await axios.put(
        `${baseUrl}/change-password`,
        data
      );
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }, []);

  const adminChangePassword = useCallback(async (userId: string, data: AdminChangePasswordRequest): Promise<void> => {
    try {
      await axios.put(
        `${baseUrl}/${userId}/change-password`,
        data
      );
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }, []);

  return {
    login,
    register,
    createUser,
    getCurrentUser,
    getUser,
    updateUser,
    updateProfile,
    deleteUser,
    listUsers,
    changePassword,
    adminChangePassword,
  };
};

export default useUserApi; 